import { computed } from 'vue';

/**
 * useSgaRenovacionPrecios
 *
 * Handles all pricing and promotion logic for SGA renewal modal.
 * Calculates discounts, formats prices, and manages promotion selections.
 *
 * @param {Object} config - Configuration object
 * @param {Ref} config.selectedPeriod - Currently selected renewal period
 * @param {Ref} config.selectedPromotions - Array of selected promotion objects
 * @param {Array} config.promotions - All available promotions (will slice to max 3)
 * @param {Function} config.formatPrice - Function to format price as currency
 *
 * @returns {Object} Reactive pricing data and methods
 */
export default function useSgaRenovacionPrecios(config) {
    const { selectedPeriod, selectedPromotions, promotions, formatPrice, selectedDiscounts, verifiedCodeDiscount } = config;

    // Normalize optional discount params
    const _selectedDiscounts = selectedDiscounts || { value: [] };
    const _verifiedCodeDiscount = verifiedCodeDiscount || { value: null };

    /**
     * Available promotions limited to max 3.
     */
    const availablePromotions = computed(() => {
        if (promotions.length > 3 && process.env.NODE_ENV === 'development') {
            console.warn(`SGA devolvió ${promotions.length} promociones, mostrando solo 3`);
        }
        return promotions.slice(0, 3);
    });

    /**
     * All active deductions (promotions + discounts + verified code).
     */
    const allActiveDeductions = computed(() => {
        const deductions = [...selectedPromotions.value];
        deductions.push(..._selectedDiscounts.value);
        if (_verifiedCodeDiscount.value) {
            deductions.push(_verifiedCodeDiscount.value);
        }
        return deductions;
    });

    /**
     * Get the percentage value from a deduction (handles both promotion and discount shapes).
     * Discount (SGA): { valueType: 'PERCENTAGE', value: 20 }
     * Discount (legacy): { percentage: 5 }
     * Promotion: { type: "Percentage", value: 10 }
     */
    const getDeductionPercentage = d => {
        // SGA discount shape: valueType === 'PERCENTAGE'
        if (d.valueType === 'PERCENTAGE' && typeof d.value === 'number') return d.value;
        // Legacy discount shape: { percentage: 5 }
        if (typeof d.percentage === 'number') return d.percentage;
        // Promotion shape: { type: "Percentage", value: 10 }
        if (d.type?.name === 'Percentage') return d.value;
        if (d.type === 'Percentage') return d.value;
        return 0;
    };

    /**
     * Get the fixed value from a deduction (handles both promotion and discount shapes).
     * Discount (SGA): { valueType: 'FIXED', value: 50 }
     * Promotion: { type: "FixedValue", value: 50 }
     */
    const getDeductionFixedValue = d => {
        // SGA discount shape: valueType === 'FIXED'
        if (d.valueType === 'FIXED' && typeof d.value === 'number') return d.value;
        if (d.type?.name === 'FixedValue') return d.value;
        if (d.type === 'FixedValue') return d.value;
        return 0;
    };

    /**
     * Check if a deduction is a percentage discount.
     */
    const isPercentageDeduction = d => {
        return d.valueType === 'PERCENTAGE'
            || typeof d.percentage === 'number'
            || d.type?.name === 'Percentage'
            || d.type === 'Percentage';
    };

    /**
     * Check if a deduction is a fixed value discount.
     */
    const isFixedValueDeduction = d => {
        return d.valueType === 'FIXED'
            || d.type?.name === 'FixedValue'
            || d.type === 'FixedValue';
    };

    /**
     * Calculate discounted price based on selected promotions and discounts.
     * ALL percentage discounts apply to the BASE price, not sequentially.
     * Fixed value discounts are then subtracted.
     */
    const discountedPrice = computed(() => {
        if (!selectedPeriod.value) return 0;
        const basePrice = selectedPeriod.value.price;
        let totalDiscount = 0;

        const deductions = allActiveDeductions.value;

        // Percentage discounts apply to BASE price (not compounded)
        deductions.forEach(d => {
            if (isPercentageDeduction(d)) {
                totalDiscount += basePrice * (getDeductionPercentage(d) / 100);
            }
        });

        // Fixed value discounts
        deductions.forEach(d => {
            if (isFixedValueDeduction(d)) {
                totalDiscount += getDeductionFixedValue(d);
            }
        });

        return Math.max(0, basePrice - totalDiscount);
    });

    /**
     * Formatted discounted price for display.
     */
    const formattedDiscountedPrice = computed(() => {
        return formatPrice(discountedPrice.value);
    });

    /**
     * Check if any discount is applied.
     */
    const hasDiscount = computed(() => {
        return allActiveDeductions.value.length > 0;
    });

    /**
     * Breakdown of each selected deduction's actual discount amount.
     * Percentage discounts calculated on BASE price (not compounded).
     */
    const selectedPromotionBreakdown = computed(() => {
        if (!selectedPeriod.value) return [];
        const basePrice = selectedPeriod.value.price;
        const breakdown = [];

        const deductions = allActiveDeductions.value;

        // Percentage discounts on base price
        deductions.forEach(d => {
            if (isPercentageDeduction(d)) {
                const amount = basePrice * (getDeductionPercentage(d) / 100);
                breakdown.push({ name: d.name, amount });
            }
        });

        // Fixed value discounts
        deductions.forEach(d => {
            if (isFixedValueDeduction(d)) {
                breakdown.push({ name: d.name, amount: getDeductionFixedValue(d) });
            }
        });

        return breakdown;
    });

    /**
     * Toggle promotion selection by index.
     */
    const togglePromotion = promotionIndex => {
        const promotion = availablePromotions.value[promotionIndex];
        if (!promotion) return;

        const index = selectedPromotions.value.findIndex(
            p => p.name === promotion.name
        );

        if (index > -1) {
            // Promotion already selected, remove it
            selectedPromotions.value.splice(index, 1);
        } else {
            // Promotion not selected, add it
            selectedPromotions.value.push(promotion);
        }
    };

    /**
     * Check if a promotion is selected by index.
     */
    const isPromotionSelected = promotionIndex => {
        const promotion = availablePromotions.value[promotionIndex];
        if (!promotion) return false;
        return selectedPromotions.value.some(p => p.name === promotion.name);
    };

    return {
        availablePromotions,
        discountedPrice,
        formattedDiscountedPrice,
        hasDiscount,
        selectedPromotionBreakdown,
        allActiveDeductions,
        togglePromotion,
        isPromotionSelected
    };
}
